//#region Imports
import React, { useState, useCallback } from "react";
import {
  Paper,
  Menu,
  MenuList,
  MenuItem,
  ListItemText,
  ListSubheader,
  Button,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { NavLink } from "react-router-dom";

import { isEmpty } from "lodash";

import { useSelector } from "react-redux";

// Icons
import KeyIcon from "@mui/icons-material/Key";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import ExtensionIcon from "@mui/icons-material/Extension";
import BookIcon from "@mui/icons-material/Book";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import BarChartIcon from "@mui/icons-material/BarChart";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import DvrIcon from "@mui/icons-material/Dvr";
import LockIcon from "@mui/icons-material/Lock";
import ArticleIcon from "@mui/icons-material/Article";

import { BASE_PATHS } from "../../../constants/pathes";
import { PERMISSION_NAMES } from "../../../constants/permissions";
//#endregion

// List of pages with public access
const publicPages = [
  {
    href: BASE_PATHS.googleAuthenticator,
    icon: <LockIcon fontSize="medium" />,
    text: "Google Authenticator",
    key: "googleAuthMenu",
  },
  {
    href: BASE_PATHS.scripts,
    icon: <ExtensionIcon fontSize="medium" />,
    text: "Scripts",
    key: "scriptsMenu",
  },
  {
    href: BASE_PATHS.manuals,
    icon: <BookIcon fontSize="medium" />,
    text: "Manuals / FAQ",
    key: "manualsMenu",
  },
  {
    href: BASE_PATHS.apiKeys,
    icon: <KeyIcon fontSize="medium" />,
    text: "API Keys",
    key: "apiKeys",
  },
];

// List of pages with specific access rights
const protectedPages = [
  {
    href: BASE_PATHS.aiModels,
    icon: <PrecisionManufacturingIcon fontSize="medium" />,
    text: "AI Models",
    key: "aiModelsMenu",
    permissionId: PERMISSION_NAMES.aiModels,
  },
  {
    href: BASE_PATHS.uiRemote,
    icon: <WifiTetheringIcon fontSize="medium" />,
    text: "UI Remote",
    key: "uiRemoteMenu",
    permissionId: PERMISSION_NAMES.uiremoteConcat,
  },
  {
    href: BASE_PATHS.dashboard,
    icon: <BarChartIcon fontSize="medium" />,
    text: "Dashboard",
    key: "dashboardMenu",
    permissionId: PERMISSION_NAMES.dashboard,
  },
  {
    href: BASE_PATHS.mappingAlias,
    icon: <MenuBookIcon fontSize="medium" />,
    text: "Mapping Alias",
    key: "aliasMenu",
    permissionId: PERMISSION_NAMES.aliases,
  },
  {
    href: BASE_PATHS.adminPanel,
    icon: <SupervisorAccountIcon fontSize="medium" />,
    text: "Admin Panel",
    key: "adminPanel",
    permissionId: PERMISSION_NAMES.users,
  },
  {
    href: BASE_PATHS.drsPipe,
    icon: <DvrIcon fontSize="medium" />,
    text: "OVS Bot Pipeline",
    key: "ovsBotPipeline",
    permissionId: PERMISSION_NAMES.drsPipe,
  },
  {
    onClick: () => (window.location.href = BASE_PATHS.apiDocs),
    icon: <ArticleIcon fontSize="medium" />,
    text: "API Docs",
    key: "apiDocs",
    permissionId: PERMISSION_NAMES.swagger,
  },
];

// Custom styling for menu buttons
const MenuButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  color: theme.palette.text.primary,
  padding: 0,
  backgroundColor: "transparent",
  "&:hover, &:active, &:focus": {
    backgroundColor: "transparent",
    boxShadow: "none",
    borderColor: "transparent",
  },
}));

/**
 * Component for displaying protected pages in the menu.
 * @param {Object} props Props of the component;
 * @param {Function} props.handleClose Function to close the parent element;
 * @returns {JSX.Element}
 */
const ProtectedPagesComponent = ({ handleClose }) => {
  //#region Hooks
  const pageIDs = useSelector((state) => state.userInfo.pages || []);
  //#endregion

  return (
    <>
      {protectedPages.map(
        (item) =>
          pageIDs.includes(item.permissionId) && (
            <MenuLink key={item.key} onClick={handleClose} {...item} />
          ),
      )}
    </>
  );
};

/**
 * Creates functional menu item element for the navigation.
 * @param {Object} props Props of the component;
 * @param {string} props.href Link of the menu item;
 * @param {JSX.Element} props.icon Icon of the menu item;
 * @param {string} props.text Label of the menu item;
 * @param {Function} props.onClick Click action function of the menu item;
 * @returns {JSX.Element}
 */
const MenuLink = ({ href, icon, text, onClick }) => (
  <MenuItem
    onClick={onClick}
    sx={{
      transition: "all 0.3s ease-out",
      borderRadius: 1,
    }}>
    <NavLink
      to={href}
      viewTransition
      style={{
        textDecoration: "none",
        color: "inherit",
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        columnGap: 8,
        padding: 1,
      }}>
      {icon}
      <ListItemText>{text}</ListItemText>
    </NavLink>
  </MenuItem>
);

/**
 * Creates navigation menu for the desktop render. Helper element.
 * @returns {JSX.Element}
 */
const NavMenu = () => {
  //#region States
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  //#endregion

  //#region Hooks
  const pages = useSelector((state) => state.userInfo.pages || []);
  //#endregion

  //#region Methods
  const handleClose = useCallback(() => setAnchorEl(null), []);
  //#endregion

  return (
    <>
      {publicPages.map((item) => (
        <MenuLink key={item.key} {...item} />
      ))}
      {isEmpty(pages) || (
        <>
          <MenuButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <MenuItem
              sx={{
                display: "flex",
                flexDirection: "row",
                columnGap: 1,
                padding: 1,
                transition: "all 0.3s ease-out",
                borderRadius: 1,
              }}>
              <MoreHorizIcon fontSize="medium" />
              <ListItemText>Your Pages</ListItemText>
            </MenuItem>
          </MenuButton>
          <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
            <ProtectedPagesComponent handleClose={handleClose} />
          </Menu>
        </>
      )}
    </>
  );
};

/**
 * Creates navigation element for the desktop render.
 * @returns {JSX.Element}
 */
export const Navigation = () => (
  <Paper
    component="nav"
    elevation={0}
    sx={{
      px: 1,
      py: 1.5,
      height: "min-content",
    }}>
    <MenuList
      sx={{ display: "flex", width: "100%", padding: "0", columnGap: 0.5 }}>
      <NavMenu />
    </MenuList>
  </Paper>
);

/**
 * Creates burger navigation menu for the mobile render.
 * @returns {JSX.Element}
 *
 */
export const BurgerMenu = () => {
  //#region States
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  //#endregion

  //#region Hooks
  const pages = useSelector((state) => state.userInfo.pages || []);
  //#endregion

  //#region Methods
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  //#endregion
  return (
    <>
      {open ? (
        <IconButton size="small" color="inherit" onClick={handleClose}>
          <MenuOpenIcon />
        </IconButton>
      ) : (
        <IconButton size="small" color="inherit" onClick={handleClick}>
          <MenuIcon />
        </IconButton>
      )}
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {publicPages.map((item) => (
          <MenuLink key={item.key} onClick={handleClose} {...item} />
        ))}
        {isEmpty(pages) || (
          <>
            <ListSubheader>Your Pages</ListSubheader>
            <ProtectedPagesComponent handleClose={handleClose} />
          </>
        )}
      </Menu>
    </>
  );
};
